import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import Toast from './Toast';
import Modal from './Modal';

/**
 * Quick-add modal for creating a new vendor inline.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onVendorCreated - Callback with the newly created vendor object
 */
export function QuickAddVendorModal({ isOpen, onClose, onVendorCreated }) {
  const [formData, setFormData] = useState({ name: '', contact_info: '', primary_contact_name: '', primary_contact_phone: '', primary_contact_email: '', fuel_surcharge_in_subtotal: false, uses_sku: true });
  const [toast, setToast] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setToast(null);
    try {
      const response = await api.createVendor(formData);
      onVendorCreated(response.data);
      setFormData({ name: '', contact_info: '', primary_contact_name: '', primary_contact_phone: '', primary_contact_email: '', fuel_surcharge_in_subtotal: false, uses_sku: true });
      onClose();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Error creating vendor', type: 'error' });
      console.error('Error:', err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Quick Add Vendor">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="quick-add-vendor-name">Vendor Name *</label>
          <input
            id="quick-add-vendor-name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            autoFocus
          />
        </div>
        <div className="quick-add__actions">
          <button type="submit">Add Vendor</button>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

QuickAddVendorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onVendorCreated: PropTypes.func.isRequired,
};

/**
 * Quick-add modal for creating a new category inline with 3-level hierarchy.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onCategoryCreated - Callback with the newly created category object
 */
export function QuickAddCategoryModal({ isOpen, onClose, onCategoryCreated }) {
  const [existingCategories, setExistingCategories] = useState([]);
  const [formData, setFormData] = useState({ level1: '', level2: '', level3: '' });
  const [isNewLevel1, setIsNewLevel1] = useState(false);
  const [isNewLevel2, setIsNewLevel2] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      // Reset form when opening
      setFormData({ level1: '', level2: '', level3: '' });
      setIsNewLevel1(false);
      setIsNewLevel2(false);
      setToast(null);
    }
  }, [isOpen]);

  const loadCategories = async () => {
    try {
      const response = await api.getCategories();
      setExistingCategories(response.data);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  // Get unique Level 1 values
  const level1Options = [...new Set(existingCategories.map(c => c.level1))].sort();

  // Get Level 2 values for selected Level 1
  const level2Options = formData.level1 && !isNewLevel1
    ? [...new Set(
        existingCategories
          .filter(c => c.level1 === formData.level1 && c.level2)
          .map(c => c.level2)
      )].sort()
    : [];

  const handleLevel1Change = (value) => {
    if (value === '__add_new__') {
      setIsNewLevel1(true);
      setFormData({ ...formData, level1: '', level2: '', level3: '' });
      setIsNewLevel2(false);
    } else {
      setIsNewLevel1(false);
      setFormData({ ...formData, level1: value, level2: '', level3: '' });
      setIsNewLevel2(false);
    }
  };

  const handleLevel2Change = (value) => {
    if (value === '__add_new__') {
      setIsNewLevel2(true);
      setFormData({ ...formData, level2: '', level3: '' });
    } else {
      setIsNewLevel2(false);
      setFormData({ ...formData, level2: value, level3: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setToast(null);
    try {
      const response = await api.createCategory(formData);
      onCategoryCreated(response.data);
      onClose();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Error creating category', type: 'error' });
      console.error('Error:', err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Quick Add Category">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="quick-add-category-level1">Level 1 (Main) *</label>
          {!isNewLevel1 && level1Options.length > 0 ? (
            <select
              id="quick-add-category-level1"
              value={formData.level1}
              onChange={(e) => handleLevel1Change(e.target.value)}
              required
              autoFocus
            >
              <option value="">Select or add new...</option>
              {level1Options.map(l1 => (
                <option key={l1} value={l1}>{l1}</option>
              ))}
              <option value="__add_new__">+ Add New Level 1...</option>
            </select>
          ) : (
            <div className="quick-add__level-row">
              <input
                id="quick-add-category-level1"
                type="text"
                value={formData.level1}
                onChange={(e) => setFormData({ ...formData, level1: e.target.value })}
                placeholder="e.g., Dairy"
                required
                autoFocus
                className="quick-add__level-input"
              />
              {level1Options.length > 0 && (
                <button
                  type="button"
                  className="btn-secondary quick-add__level-add-btn"
                  onClick={() => {
                    setIsNewLevel1(false);
                    setFormData({ ...formData, level1: '', level2: '', level3: '' });
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="quick-add-category-level2">Level 2 (Sub)</label>
          {!isNewLevel2 && level2Options.length > 0 ? (
            <select
              id="quick-add-category-level2"
              value={formData.level2}
              onChange={(e) => handleLevel2Change(e.target.value)}
            >
              <option value="">Select or add new...</option>
              {level2Options.map(l2 => (
                <option key={l2} value={l2}>{l2}</option>
              ))}
              <option value="__add_new__">+ Add New Level 2...</option>
            </select>
          ) : (
            <div className="quick-add__level-row">
              <input
                id="quick-add-category-level2"
                type="text"
                value={formData.level2}
                onChange={(e) => setFormData({ ...formData, level2: e.target.value })}
                placeholder="e.g., Milks"
                className="quick-add__level-input"
              />
              {level2Options.length > 0 && (
                <button
                  type="button"
                  className="btn-secondary quick-add__level-add-btn"
                  onClick={() => {
                    setIsNewLevel2(false);
                    setFormData({ ...formData, level2: '' });
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="quick-add-category-level3">Level 3 (Detail)</label>
          <input
            id="quick-add-category-level3"
            type="text"
            value={formData.level3}
            onChange={(e) => setFormData({ ...formData, level3: e.target.value })}
            placeholder="e.g., Oat Milk"
          />
        </div>

        <div className="quick-add__actions">
          <button type="submit">Add Category</button>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

QuickAddCategoryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCategoryCreated: PropTypes.func.isRequired,
};

/**
 * Quick-add modal for creating a new SKU in the catalog inline.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onSKUCreated - Callback with the newly created SKU object
 * @param {string|number} [props.vendorId] - Vendor ID to associate the SKU with
 * @param {string} [props.vendorName] - Vendor name displayed in the modal header
 * @param {Array} [props.categories] - Available categories for the dropdown
 */
export function QuickAddSKUModal({ isOpen, onClose, onSKUCreated, vendorId, vendorName, categories }) {
  const [formData, setFormData] = useState({
    sku: '',
    item_name: '',
    category_id: '',
    default_uom: ''
  });
  const [toast, setToast] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setToast(null);

    if (!vendorId) {
      setToast({ message: 'Please select a vendor first', type: 'error' });
      return;
    }

    try {
      const response = await api.createSKU({ ...formData, vendor_id: vendorId });
      onSKUCreated(response.data);
      setFormData({ sku: '', item_name: '', category_id: '', default_uom: '' });
      onClose();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Error creating SKU', type: 'error' });
      console.error('Error:', err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Quick Add SKU to Catalog">
      {vendorName && (
        <p className="quick-add__subtitle">
          Adding SKU for vendor: <strong>{vendorName}</strong>
        </p>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="quick-add-sku-code">SKU Code *</label>
          <input
            id="quick-add-sku-code"
            type="text"
            value={formData.sku}
            onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
            required
            autoFocus
          />
        </div>
        <div className="form-group">
          <label htmlFor="quick-add-sku-item-name">Item Name *</label>
          <input
            id="quick-add-sku-item-name"
            type="text"
            value={formData.item_name}
            onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="quick-add-sku-category">Category</label>
          <select
            id="quick-add-sku-category"
            value={formData.category_id}
            onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
          >
            <option value="">Select category...</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.full_path}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="quick-add-sku-uom">Default UOM</label>
          <select
            id="quick-add-sku-uom"
            value={formData.default_uom}
            onChange={(e) => setFormData({ ...formData, default_uom: e.target.value })}
          >
            <option value="">-</option>
            <option value="each">Each</option>
            <option value="case">Case</option>
            <option value="box">Box</option>
            <option value="lb">Pound</option>
            <option value="kg">Kilogram</option>
            <option value="gal">Gallon</option>
            <option value="liter">Liter</option>
            <option value="dozen">Dozen</option>
          </select>
        </div>
        <div className="quick-add__actions">
          <button type="submit">Add SKU</button>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

QuickAddSKUModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSKUCreated: PropTypes.func.isRequired,
  vendorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  vendorName: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    full_path: PropTypes.string,
  })),
};
